import React, { useState, useEffect } from "react";
import {
  Download,
  Share2,
  XCircle,
  Trash2,
  RefreshCcw,
  Menu,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Edit2
} from "lucide-react";
import { useServiceStatus } from "../context/ServiceStatusContext";

const Navbar5 = ({
  pageTitle,
  onDownload,
  onClose,
  onRevise,
  onDelete,
  versionIndex,
  totalVersions,
  onPrev,
  onNext,
  onShare,
  onRestart,
  onMedia,
  showMediaIcon = false,
  onTitleSave //  ⬅ new prop
}) => {
  const { serviceDown } = useServiceStatus();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  /* ------------------------------------------------------------------ */
  /*  Inline-rename local state                                          */
  /* ------------------------------------------------------------------ */
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(pageTitle);

  // Keep editedTitle in sync with prop
  useEffect(() => setEditedTitle(pageTitle), [pageTitle]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveTitle();
    if (e.key === "Escape") cancelEdit();
  };

  const saveTitle = () => {
    const trimmed = editedTitle.trim();
    setIsEditingTitle(false);
    if (trimmed && trimmed !== pageTitle) onTitleSave?.(trimmed);
  };

  const cancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle(pageTitle);
  };

  /* ------------------------------------------------------------------ */
  /*  Track window resize to toggle mobile/desktop layout                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 768) setShowMobileMenu(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  // Instagram-like rainbow background for the media icon
  const rainbowBackgroundStyle = {
    background:
      "linear-gradient(to top right,rgb(255, 237, 34),rgb(251, 230, 0) 5%, #fd5949 45%, #d6249f 60%, #285aeb 90%)",
    color: "white"
  };

  return (
    <nav className="flex flex-col bg-white border-b">
      {/* ───────────────────────────────────────────────────────── Main row */}
      <div className="flex items-center justify-between p-4 md:p-6 w-full">
        {/* ░░ Title + rename ░░ */}
        <div className="flex items-center space-x-2 truncate max-w-[200px] md:max-w-none">
          {isEditingTitle ? (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={handleKeyDown}
              className="border-b border-gray-300 focus:outline-none focus:border-black text-xl font-bold truncate"
              autoFocus
            />
          ) : (
            <>
              <button
                onClick={() => setIsEditingTitle(true)}
                className="p-1 text-gray-600 hover:text-black"
                title="Rename view"
              >
                <Edit2 size={20} />
              </button>
              <span className="text-xl font-bold text-black truncate">
                {pageTitle}
              </span>
            </>
          )}
        </div>

        {/* ░░ Toolbar ░░ */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/*  Version navigation  */}
          <div className="flex items-center space-x-1 md:space-x-2 mr-2">
            <button
              onClick={onPrev}
              disabled={versionIndex === 0}
              className="p-1 md:px-3 md:py-1 bg-black text-white rounded disabled:opacity-50"
              aria-label="Previous version"
            >
              {isMobile ? <ChevronLeft size={18} /> : "<"}
            </button>

            <span className="text-gray-700 text-sm md:text-base">
              {versionIndex + 1}/{totalVersions}
            </span>

            <button
              onClick={onNext}
              disabled={versionIndex === totalVersions - 1}
              className="p-1 md:px-3 md:py-1 bg-black text-white rounded disabled:opacity-50"
              aria-label="Next version"
            >
              {isMobile ? <ChevronRight size={18} /> : ">"}
            </button>
          </div>

          {/* Desktop controls */}
          {!isMobile && (
            <>
              <button
                onClick={serviceDown ? undefined : onRevise}
                disabled={serviceDown}
                className={`px-3 py-1 bg-black text-white rounded whitespace-nowrap ${
                  serviceDown ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={serviceDown ? "Service is currently down" : "Revise this version"}
              >
                Revise this version
              </button>

              {showMediaIcon && (
                <button
                  onClick={onMedia}
                  className="p-1 rounded"
                  style={rainbowBackgroundStyle}
                  title="Add media"
                  aria-label="Add media"
                >
                  <ImageIcon size={24} />
                </button>
              )}

              <button
                onClick={onRestart}
                className="text-gray-600 hover:text-black transition-colors duration-200"
                title="Restart/Refresh HTML"
              >
                <RefreshCcw size={24} />
              </button>

              <button
                onClick={onDownload}
                className="text-gray-600 hover:text-black transition-colors duration-200"
                title="Download HTML"
              >
                <Download size={24} />
              </button>

              <button
                onClick={onShare}
                className="text-gray-600 hover:text-black transition-colors duration-200"
                title="Share"
              >
                <Share2 size={24} />
              </button>

              <button
                onClick={onDelete}
                className="text-gray-600 hover:text-red-600 transition-colors duration-200"
                title="Delete this version"
              >
                <Trash2 size={24} />
              </button>

              <button
                onClick={onClose}
                className="text-gray-600 hover:text-black transition-colors duration-200"
                title="Close"
              >
                <XCircle size={24} />
              </button>
            </>
          )}

          {/*  Mobile menu toggle  */}
          {isMobile && (
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="text-gray-600 hover:text-black transition-colors duration-200 ml-2"
              aria-label={showMobileMenu ? "Close menu" : "Open menu"}
            >
              <Menu size={24} />
            </button>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────── Mobile dropdown */}
      {isMobile && showMobileMenu && (
        <div className="flex flex-col p-4 border-t bg-gray-50 animate-fade-in-down">
          <button
            onClick={serviceDown ? undefined : onRevise}
            disabled={serviceDown}
            className={`flex items-center justify-between p-3 mb-2 bg-gray-100 hover:bg-gray-200 rounded ${
              serviceDown ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span className="font-medium">Revise this version</span>
          </button>

          <div className="grid grid-cols-2 gap-2">
            {showMediaIcon && (
              <button
                onClick={onMedia}
                className="p-1 rounded flex items-center justify-center"
                style={rainbowBackgroundStyle}
                title="Add media"
                aria-label="Add media"
              >
                <ImageIcon size={20} />
              </button>
            )}

            <button
              onClick={onRestart}
              className="flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded"
            >
              <span>Restart</span>
              <RefreshCcw size={20} />
            </button>

            <button
              onClick={onDownload}
              className="flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded"
            >
              <span>Download</span>
              <Download size={20} />
            </button>

            <button
              onClick={onShare}
              className="flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded"
            >
              <span>Share</span>
              <Share2 size={20} />
            </button>

            <button
              onClick={onDelete}
              className="flex items-center justify-between p-3 bg-gray-100 hover:bg-red-100 text-red-600 hover:bg-red-200 rounded"
            >
              <span>Delete</span>
              <Trash2 size={20} />
            </button>
          </div>

          <button
            onClick={onClose}
            className="flex items-center justify-between p-3 mt-2 bg-gray-100 hover:bg-gray-200 rounded"
          >
            <span>Close</span>
            <XCircle size={20} />
          </button>
        </div>
      )}

      {/*  tiny fade-in animation */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.2s ease-out;
        }
      `}</style>
    </nav>
  );
};

export default Navbar5;